import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { CustomerSettingsI } from '@interfaces/customer-settings.interface';
import { ChannelTypeE } from '@enums/channel.enum';

/**
 * Holds runtime feature flags derived from the current customer settings
 * (loaded once at boot via CustomersService.getcustomerSettingsById).
 *
 * The most relevant flag for the 360dialog integration is `exclusiveWhatsappOficial`
 * — when true, the UI must hide every reference to WhatsApp Web / WhatsApp Omni.
 */
@Injectable({ providedIn: 'root' })
export class CustomerFeatureFlagsService {
  private readonly settingsSubject = new BehaviorSubject<CustomerSettingsI | null>(null);
  public readonly settings$ = this.settingsSubject.asObservable();

  public readonly exclusiveWhatsappOficial$ = this.settings$.pipe(
    map((s) => Boolean(s?.exclusiveWhatsappOficial || s?.dialog360?.exclusiveWhatsappOficial)),
    distinctUntilChanged()
  );

  public setSettings(settings: CustomerSettingsI | null): void {
    this.settingsSubject.next(settings);
  }

  public clear(): void {
    this.settingsSubject.next(null);
  }

  public get exclusiveWhatsappOficial(): boolean {
    const s = this.settingsSubject.value;
    return Boolean(s?.exclusiveWhatsappOficial || s?.dialog360?.exclusiveWhatsappOficial);
  }

  public get useExternalChat(): boolean {
    return Boolean(this.settingsSubject.value?.useExternalChat);
  }

  /**
   * Returns true when the given channel type must be hidden in the UI for
   * the current customer. Currently the only restriction comes from
   * `exclusiveWhatsappOficial`, which hides every non-official WhatsApp variant.
   */
  public isChannelTypeHidden(type: ChannelTypeE | string | undefined | null): boolean {
    if (!type) return false;
    if (this.exclusiveWhatsappOficial && type === ChannelTypeE.WHATSAPP) {
      return true;
    }
    // WhatsOmni (external-chat) is opt-in: hide the card unless the customer has it.
    if (!this.useExternalChat && type === ChannelTypeE.EXTERNAL_CHAT) {
      return true;
    }
    return false;
  }

  /** Filter helper for arrays of objects with a `type` property */
  public filterChannelList<T extends { type?: ChannelTypeE | string }>(items: T[] | null | undefined): T[] {
    if (!items) return [];
    return items.filter((it) => !this.isChannelTypeHidden(it.type));
  }
}
